import {
  Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CustomFlexLayoutModule } from 'src/app/shared/flex-layout/custom-flex-layout.module';
import { CrmTaskService } from 'src/app/services/crm-task.service';

/**
 * CrmTaskPanelComponent
 *
 * Full task management panel for a CRM resource.
 *
 * Usage:
 *   <calsoft-crm-task-panel
 *     resourceType="LEAD"
 *     [resourceId]="lead.entityId"
 *     [currentUser]="currentUser"
 *     [userList]="users">
 *   </calsoft-crm-task-panel>
 */
@Component({
  selector: 'calsoft-crm-task-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatChipsModule,
    MatBadgeModule,
    CustomFlexLayoutModule
  ],
  templateUrl: './crm-task-panel.component.html',
  styleUrls: ['./crm-task-panel.component.scss']
})
export class CrmTaskPanelComponent implements OnInit, OnDestroy {

  @Input() resourceType = 'LEAD';
  @Input() resourceId!: number;
  @Input() currentUser: any = {};
  @Input() userList: any[] = [];

  tasks: any[] = [];
  showCreateForm = false;
  saving = false;
  page = 0;
  size = 20;
  totalPages = 0;

  taskForm!: FormGroup;

  priorities = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
  statusColors: Record<string, string> = {
    OPEN: '#2196F3',
    IN_PROGRESS: '#FF9800',
    COMPLETED: '#4CAF50',
    OVERDUE: '#F44336'
  };

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private crmTaskService: CrmTaskService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.taskForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      assignedTo: [null, Validators.required],
      assignedToName: [''],
      dueDateTime: [null, Validators.required],
      priority: ['MEDIUM'],
      reminderBeforeMinutes: [30]
    });
    this.loadTasks();
  }

  loadTasks(): void {
    this.crmTaskService
      .getTasksByResource(this.resourceType, this.resourceId, this.page, this.size)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.tasks = res.content ?? res;
        this.totalPages = res.totalPages ?? 1;
        this.cdr.markForCheck();
      });
  }

  toggleCreateForm(): void {
    this.showCreateForm = !this.showCreateForm;
    if (!this.showCreateForm) this.taskForm.reset({ priority: 'MEDIUM', reminderBeforeMinutes: 30 });
  }

  onAssigneeChange(userId: any): void {
    const user = this.userList.find(u => u.id === userId || u.userId === userId);
    if (user) {
      this.taskForm.patchValue({ assignedToName: user.name || user.userName });
    }
  }

  submitTask(): void {
    if (this.taskForm.invalid || this.saving) return;
    this.saving = true;

    const payload = {
      ...this.taskForm.value,
      resourceType: this.resourceType,
      resourceId: this.resourceId,
      assignedBy: this.currentUser?.id,
      assignedByName: this.currentUser?.name ?? ''
    };

    this.crmTaskService.createTask(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.showCreateForm = false;
          this.taskForm.reset({ priority: 'MEDIUM', reminderBeforeMinutes: 30 });
          this.loadTasks();
        },
        error: () => { this.saving = false; this.cdr.markForCheck(); }
      });
  }

  completeTask(taskId: number): void {
    this.crmTaskService.completeTask(taskId, this.currentUser?.name ?? 'system')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const t = this.tasks.find(x => x.id === taskId);
        if (t) { t.status = 'COMPLETED'; this.cdr.markForCheck(); }
      });
  }

  deleteTask(taskId: number): void {
    this.crmTaskService.deleteTask(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.tasks = this.tasks.filter(t => t.id !== taskId);
        this.cdr.markForCheck();
      });
  }

  statusColor(status: string): string {
    return this.statusColors[status] ?? '#9E9E9E';
  }

  trackByTaskId(_: number, task: any): number {
    return task.id;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
